/**
 * Copyright (c) 2023 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import { FilterParams } from 'src/shared/types/pipeline';

export function defaultPipelineFilters(isSuperAdmin: boolean): FilterParams {
  // Default to showing only scheduled for today
  return {
    scheduledForToday: true,
    scheduledForTomorrow: false,
    scheduledForYesterday: false,
    scheduledUpcoming: false,
    scheduledInPast: false,
    status: ['CREATED', 'NEEDS_ATTENTION', 'DRAFT'],
    organizations: [],
    groups: isSuperAdmin ? ['Blend Notary'] : [],
    assignmentStatus: ['DONE', 'PENDING'],
    page: 0,
    appointmentStatus: [],
    lenderDocuments: [],
    titleDocuments: [],
  };
}

export function clearedPipelineFilters(isSuperAdmin: boolean): FilterParams {
  return {
    ...defaultPipelineFilters(isSuperAdmin),
    groups: [],
    scheduledForToday: false,
    status: [],
    assignmentStatus: [],
  };
}

export function updateablePipelineFilters(isSuperAdmin: boolean) {
  return Object.keys(defaultPipelineFilters(isSuperAdmin));
}

/**
 * Converts the filter string values from the URL to the correct type
 * @returns
 */
function convertFilterStringValues(key: string, params: URLSearchParams) {
  switch (key) {
    case 'status':
    case 'organizations':
    case 'groups':
    case 'assignmentStatus':
    case 'appointmentStatus':
    case 'lenderDocuments':
    case 'titleDocuments':
      const value = params.getAll(key);
      return value.filter(v => v !== '');
    case 'page':
      return parseInt(params.get(key) || '0', 10);
    default:
      return params.get(key) === 'true';
  }
}

/**
 * Adds missing fields to the filter params
 */
function addMissingFields(params: Partial<FilterParams>, isSuperAdmin: boolean): FilterParams {
  const updatedParams: any = { ...params };
  for (const key of updateablePipelineFilters(isSuperAdmin) as (keyof FilterParams)[]) {
    if (updatedParams[key] == null) {
      updatedParams[key] = clearedPipelineFilters(isSuperAdmin)[key];
    }
  }
  return updatedParams;
}

/**
 * Converts the filter string from the URL to a FilterParams object
 */
export function convertURLSearchToFilterParams(filterString: string, isSuperAdmin: boolean): FilterParams {
  const params = new URLSearchParams(filterString);
  const updateFilters: Record<string, number | boolean | string[]> = {};
  for (const key of params.keys()) {
    if (updateablePipelineFilters(isSuperAdmin).includes(key)) {
      updateFilters[key] = convertFilterStringValues(key, params);
    }
  }
  return addMissingFields(updateFilters, isSuperAdmin);
}

/**
 * Convert Filter params object to URL Search string
 */
export function convertFilterParamsToURLSearch(params: FilterParams, isSuperAdmin: boolean): string {
  const filters = addMissingFields(params, isSuperAdmin);
  const filterString = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (Array.isArray(value)) {
      for (const v of value) {
        if (v) {
          filterString.append(key, v.toString());
        }
      }
      continue;
    }
    // reset page to 0 when filters are reset
    if (key === 'page') {
      filterString.append(key, '0');
    } else {
      filterString.append(key, value.toString());
    }
  }
  return `${filterString.toString()}`;
}

/*
 * Update URL queries with filters
 */
export function updateUrlSearch(url: URL, urlFilters: FilterParams) {
  Object.keys(urlFilters).forEach((key: any) => {
    const value = (urlFilters as any)[key];
    if (Array.isArray(value)) {
      url.searchParams.delete(key); // Remove the key if it already exists
      value.forEach(item => url.searchParams.append(key, item));
    } else if (value !== null && value !== undefined) {
      url.searchParams.set(key, value.toString());
    }
  });
}
